import clsx from "clsx";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";

import { CheckboxList } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

export interface InputData {
  options: string[];
  answer: string[];
}

interface CheckboxItem {
  id: string;
  title: string;
  defaultChecked?: boolean;
  secondaryTitle?: string;
}

interface CheckboxListFieldProps {
  inputJson: InputData;
  /**
   * Callback that receives an InputData object:
   * { options: string[], answer: string[] }
   */
  onChange: (json: InputData) => Promise<void>;
  actionButtonMenu: PopoverMenuItem[];
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
  };
  displayOnlyMode: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
}

export const CheckboxListField: React.FC<CheckboxListFieldProps> = ({
  inputJson,
  onChange,
  actionButtonMenu,
  permissions,
  displayOnlyMode = false,
  disabled = false,
  onBlur,
  className,
}) => {
  const convertOptions = (opts: string[]): CheckboxItem[] =>
    opts.map((o) => ({
      id: o,
      title: o,
      defaultChecked: false,
    }));

  const [options, setOptions] = useState<CheckboxItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (Array.isArray(inputJson.options)) {
      const converted = convertOptions(inputJson.options);
      const updatedOptions = converted.map((item) => ({
        ...item,
        defaultChecked: inputJson.answer.includes(item.id),
      }));
      setOptions(updatedOptions);
    } else {
      console.error("Invalid inputJson: 'options' must be an array");
      setOptions([]);
    }
    if (Array.isArray(inputJson.answer)) {
      setSelectedIds(inputJson.answer);
    } else {
      console.error("Invalid inputJson: 'answer' must be an array");
      setSelectedIds([]);
    }
  }, [inputJson]);

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    let updatedSelected: string[];
    if (checked) {
      updatedSelected = [...selectedIds, value];
    } else {
      updatedSelected = selectedIds.filter((id) => id !== value);
    }
    setSelectedIds(updatedSelected);

    const output: InputData = {
      options: options.map((item) => item.title),
      answer: updatedSelected,
    };

    onChange(output);
  };

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (onBlur) {
          onBlur();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onBlur]);

  return (
    <div ref={containerRef} className={clsx("group relative pr-6", className)}>
      <div data-testid="checkbox-input-container">
        <div className="relative overflow-auto">
          <CheckboxList
            items={options}
            title=""
            legend="Options"
            orientation={options.length > 3 ? "vertical" : "horizontal"}
            onChange={handleCheckboxChange}
            disabled={displayOnlyMode || disabled}
            variant={displayOnlyMode ? "default" : "report"}
            onBlur={onBlur}
            inputClassName={displayOnlyMode ? "disabled:checked:bg-rio-600 disabled:checked:border-rio-600" : undefined}
          />
        </div>
      </div>
      <div
        data-testid="checkbox-actions-panel"
        className={`group absolute -right-1 top-1 flex-col gap-y-4 ${
          permissions.canEdit ? "opacity-0 group-hover:opacity-100" : "pointer-events-none opacity-0"
        } mt-2 transition-all`}
      >
        {actionButtonMenu.length ? (
          <PopoverMenu mode="secondary" menuItems={actionButtonMenu ?? []} openDirection="to-bottom-left" />
        ) : null}
      </div>
    </div>
  );
};
